import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { Line, OrbitControls } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";

import * as THREE from "three";

//import ContentLoader from "@/components/content-loader/content-loader";
// import { useSessionDetailsContext } from "@/context/session-details/session-details";

import type { ChartState } from "@/types/timeline-chart";

import { average, latLongToXYZ } from "../../utils";

const dummyActual: { lat: any; lon: any; alt: any }[] = [];
const dummyExpected: { lat: any; lon: any; alt: any }[] = [];

//Arrow component to show the direction of circuit
const Arrow: React.FC<{ position: THREE.Vector3; color: string; direction: string }> = ({
    position,
    color,
    direction,
}) => {
    const arrowShape = new THREE.Shape();

    if (direction === "left") {
        // Top vertex
        arrowShape.moveTo(-10, 0);
        // Bottom right vertex
        arrowShape.lineTo(10, 10);
        // Middle vertex
        arrowShape.lineTo(0, 0);
        // Bottom left vertex
        arrowShape.lineTo(10, -10);
        // Close the arrow
        arrowShape.lineTo(-10, 0);
    } else if (direction === "right") {
        arrowShape.moveTo(10, 0);
        arrowShape.lineTo(-10, -10);
        arrowShape.lineTo(0, 0);
        arrowShape.lineTo(-10, 10);
        arrowShape.lineTo(10, 0);
    }

    const arrowGeometry = new THREE.ShapeGeometry(arrowShape);

    return (
        /* eslint-disable */
        <mesh position={position} rotation={[Math.PI / 2, 0, -Math.PI / 4]}>
            <bufferGeometry attach="geometry" {...arrowGeometry} />
            <meshBasicMaterial color={color} side={THREE.DoubleSide} />
        </mesh>
        /* eslint-enable */
    );
};

// Plane component for runway - first point from data
const RunwayPlane: React.FC<{ position: THREE.Vector3 }> = ({ position }) => {
    return (
        /* eslint-disable */
        <>
            <mesh position={[position.x, position.y - 5, position.z]} rotation={[0, 10, 0]}>
                <boxGeometry args={[120, 1, 25]} />
                <meshBasicMaterial color="#121212" side={THREE.DoubleSide} />
            </mesh>
        </>
        /* eslint-enable */
    );
};

type CircuitProps = {
    chartStateByType: ChartState;
};

interface ThreeSceneHandle {
    resetCamera: () => void;
}

const FlightPathModel = forwardRef<ThreeSceneHandle, CircuitProps>(function Circuit3D(
    { chartStateByType },
    ref,
) {
    // const { _flightPathData, isFetchingFlightPath } = useSessionDetailsContext();

    //const sessionCircuitFlightPathData = flightPathData?.find(
    //    (d: any) => d.code === "session-flight-path",
    //);

    // Active camera state
    const cameraRef = useRef<THREE.PerspectiveCamera | THREE.OrthographicCamera | undefined>(
        undefined,
    );
    const [camera, setCamera] = useState<
        THREE.PerspectiveCamera | THREE.OrthographicCamera | undefined
    >(cameraRef.current);

    const [usePerspective, setUsePerspective] = useState(false);

    //State for THREE.MOUSE type.  This can be DOLLY, PAN or ROTATE
    //This needs to change so that you aren't trying to 3D rotate in 2D.
    const [mouseCamera, setMouseCamera] = useState<THREE.MOUSE | undefined>(THREE.MOUSE.PAN);
    const [allowRotate, setAllowRotate] = useState(false);
    const [screenPan, setScreenPan] = useState(true);
    const orbitRef = useRef<any>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const perspectiveCamera = new THREE.PerspectiveCamera(30);
    const orthographicCamera = new THREE.OrthographicCamera(
        // Left boundary
        -window.innerWidth / (2 * 4),
        // Right boundary
        window.innerWidth / (2 * 4),
        // Top boundary
        window.innerHeight / (2 * 4),
        // Bottom boundary
        -window.innerHeight / (2 * 4),
        // near and far clipping planes
        1,
        1000,
    );

    // Define the resetCamera function and expose it to the parent
    useImperativeHandle(ref, () => ({
        resetCamera() {
            orbitRef.current.target.set(0, 0, 0);
            orbitRef.current.update();
            toggleCamera();
        },
    }));

    const actualPoints = dummyActual.map(({ lat, lon, alt }) => {
        const [x, y, z] = latLongToXYZ(lat, lon, alt);

        return new THREE.Vector3(x, y, z);
    });

    const expectedPoints = dummyExpected.map(({ lat, lon, alt }) => {
        const [x, y, z] = latLongToXYZ(lat, lon, alt);

        return new THREE.Vector3(x, y, z);
    });

    const [centerX, centerY, centerZ] = ["x", "y", "z"].map((axis) => average(actualPoints, axis));

    //Translate the points to set a new scale as circuit area is small and invisible
    //With these new points we calculate the camera position for x,z axis - it may differ to each client
    actualPoints.forEach((actualPoint) => {
        actualPoint.setX((actualPoint.x - centerX) * 100);
        actualPoint.setY(actualPoint.y - centerY);
        actualPoint.setZ((actualPoint.z - centerZ) * 100);
    });

    expectedPoints.forEach((expectedPoint) => {
        expectedPoint.setX((expectedPoint.x - centerX) * 100);
        expectedPoint.setY(expectedPoint.y - centerY);
        expectedPoint.setZ((expectedPoint.z - centerZ) * 100);
    });

    //getting the path direction based on x value ascending/descending
    const getDirection = (actualPoints: THREE.Vector3[]) => {
        return actualPoints[0].x > actualPoints[10].x ? "left" : "right";
    };

    const resetCameraValues = (customCamera: THREE.Camera) => {
        if (customCamera instanceof THREE.PerspectiveCamera) {
            customCamera.position.set(-64.35, 55.86, 158.9);
            customCamera.rotation.set(-0.34, -0.36, -0.12);
            customCamera.zoom = 1;
            customCamera.updateProjectionMatrix();
        } else if (customCamera instanceof THREE.OrthographicCamera) {
            customCamera.position.set(Math.cos(0.9), 10, Math.sin(0.9));
            customCamera.lookAt(0, 0, 0);
            customCamera.zoom = 1.05;
            customCamera.updateProjectionMatrix();
        }
    };

    // mouse buttons for camera control
    const mouseButtons = {
        LEFT: mouseCamera,
        MIDDLE: THREE.MOUSE.DOLLY,
        RIGHT: mouseCamera,
    };

    //Switch between perspective and orthographic camera
    const toggleCamera = () => {
        setUsePerspective((prev) => !prev);
        const newCamera = usePerspective ? orthographicCamera : perspectiveCamera;
        const newMouseCamera = usePerspective ? THREE.MOUSE.PAN : THREE.MOUSE.ROTATE;
        resetCameraValues(newCamera);

        cameraRef.current = newCamera;
        setMouseCamera(newMouseCamera);
        setAllowRotate(!allowRotate);
        setScreenPan(!screenPan);
        setCamera(cameraRef.current);
    };

    useEffect(() => {
        if (!cameraRef.current) {
            const initialCamera = usePerspective ? perspectiveCamera : orthographicCamera;
            resetCameraValues(initialCamera);
            //Initial zoom needs to be closer for some reason.
            //After a toggle it should be 1.
            //This may have something to do with the 2 render loops not syncing.
            initialCamera.zoom = 1.4;
            initialCamera.updateProjectionMatrix();
            cameraRef.current = initialCamera;
            setCamera(cameraRef.current);
        }
    }, []);

    useEffect(() => {
        //Listen for window resize event
        window.addEventListener("resize", handleResize);

        handleResize();

        return () => window.removeEventListener("resize", handleResize);
    }, [camera]);

    // Adjust camera aspect ratio if window is resized
    const handleResize = () => {
        if (cameraRef.current instanceof THREE.PerspectiveCamera) {
            cameraRef.current.aspect = window.innerWidth / window.innerHeight;
            cameraRef.current.updateProjectionMatrix();
        } else if (cameraRef.current instanceof THREE.OrthographicCamera) {
            cameraRef.current.left = -window.innerWidth / (2 * 4);
            cameraRef.current.right = window.innerWidth / (2 * 4);
            cameraRef.current.top = window.innerHeight / (2 * 4);
            cameraRef.current.bottom = -window.innerHeight / (2 * 4);
            cameraRef.current.updateProjectionMatrix();
        }
    };

    //if (isFetchingFlightPath) {
    //    return <ContentLoader height="28.0625rem" />;
    // }

    return (
        <Canvas ref={canvasRef} camera={cameraRef.current} style={{ height: "300px" }}>
            {/* Add lighting to make sure objects are visible */}
            <ambientLight intensity={0.5} />
            <pointLight position={[10, 10, 10]} />

            {/* Add grid helper and axis helper for visual debugging */}
            {/* <gridHelper args={[1000, 20]} /> */}
            {/* <axesHelper args={[1000]} /> */}

            {/* Line to show circuit path and arrow to show direction */}
            {chartStateByType.ActualPath && (
                <>
                    <Line points={actualPoints} color="#B3B3B3" lineWidth={2} />;
                    <Arrow
                        position={actualPoints[0]}
                        color="#B3B3B3"
                        direction={getDirection(actualPoints)}
                    />
                </>
            )}

            {/* Line to show expected circuit path and arrow to show direction */}
            {chartStateByType.ExpectedPath && (
                <>
                    <Line
                        points={expectedPoints}
                        color="#EE7127"
                        lineWidth={2}
                        dashed
                        dashSize={2}
                        gapSize={2}
                    />
                    <Arrow
                        position={expectedPoints[120]}
                        color="#EE7127"
                        direction={getDirection(expectedPoints)}
                    />
                </>
            )}

            <RunwayPlane position={actualPoints[0]} />

            {/* Controls to orbit around the scene */}
            <OrbitControls
                mouseButtons={mouseButtons}
                ref={orbitRef}
                enablePan
                minDistance={50}
                maxDistance={650}
                enableRotate={allowRotate}
                screenSpacePanning={screenPan}
            />
        </Canvas>
    );
});

export default FlightPathModel;
